/**
 * Card prodotto del pannello destro di RegisterSplitLayout.
 * Immagini e frasi IDENTICHE alle card del carosello funzionalità del sito
 * (website-next FeaturesCarousel + messages it/en.json → homeV2.features.cards).
 * Le webp sono i ritagli "v2" (device ancorato in alto a sinistra che sborda
 * in basso a destra), copiate in public/register-cards/.
 */
export type RegisterCardKey =
  | 'quote' | 'lead' | 'projects' | 'marketing' | 'automations' | 'whatsapp'
  | 'google' | 'portal' | 'provvigioni' | 'prenotazioni' | 'newsletter'
  | 'form' | 'qrcode' | 'video' | 'wiki' | 'invoicing';

export interface RegisterCardCopy {
  kicker: string;
  title: string;
  desc: string;
}

export interface RegisterCard {
  key: RegisterCardKey;
  src: string;
  alt: string;
  it: RegisterCardCopy;
  en: RegisterCardCopy;
}

export const REGISTER_CARDS: RegisterCard[] = [
  {
    key: 'quote',
    src: '/register-cards/card-quote.webp',
    alt: 'Creazione di un preventivo in Flusia',
    it: { kicker: "PREVENTIVI CON L'AI", title: 'Descrivi il lavoro. Il preventivo è pronto.', desc: 'Due modi, stesso risultato: un preventivo professionale, dentro Flusia, in un attimo.' },
    en: { kicker: 'AI QUOTES', title: 'Describe the job. The quote is ready.', desc: 'Two ways, same result: a professional quote, inside Flusia, in seconds.' },
  },
  {
    key: 'lead',
    src: '/register-cards/card-lead.webp',
    alt: 'Pipeline lead in vista Kanban nel CRM di Flusia',
    it: { kicker: 'ACQUISIZIONE LEAD', title: 'I lead entrano da ogni canale.', desc: 'Meta, form, QR e sondaggi: tutto finisce nel CRM, da solo. Nessun copia-incolla.' },
    en: { kicker: 'LEAD CAPTURE', title: 'Leads come in from every channel.', desc: 'Meta, forms, QR and surveys: everything lands in the CRM, on its own. No copy-paste.' },
  },
  {
    key: 'projects',
    src: '/register-cards/card-projects.webp',
    alt: 'Gestione progetti nel CRM di Flusia',
    it: { kicker: 'GESTIONE PROGETTI', title: 'Sai cosa sta andando a fuoco. Adesso.', desc: 'Prima che un ritardo diventi un problema, lo vedi. War Room in tempo reale.' },
    en: { kicker: 'PROJECT MANAGEMENT', title: 'You know what is on fire. Right now.', desc: 'You see a delay before it becomes a problem. Real-time War Room.' },
  },
  {
    key: 'marketing',
    src: '/register-cards/card-marketing.webp',
    alt: 'Dashboard Marketing Intelligence di Flusia',
    it: { kicker: 'MARKETING INTELLIGENCE', title: 'Quanto rende davvero ogni euro.', desc: 'Dalla campagna al cliente pagante, tutto tracciato in un posto solo.' },
    en: { kicker: 'MARKETING INTELLIGENCE', title: 'What every euro really returns.', desc: 'From campaign to paying customer, everything tracked in one place.' },
  },
  {
    key: 'automations',
    src: '/register-cards/card-automations.webp',
    alt: 'Automazioni e workflow in Flusia',
    it: { kicker: 'AUTOMAZIONI', title: 'Il lavoro ripetitivo si fa da solo.', desc: 'Quando succede X, Flusia fa Y. Trigger e azioni, senza muovere un dito.' },
    en: { kicker: 'AUTOMATIONS', title: 'Repetitive work does itself.', desc: 'When X happens, Flusia does Y. Triggers and actions, without lifting a finger.' },
  },
  {
    key: 'whatsapp',
    src: '/register-cards/card-whatsapp.webp',
    alt: 'WhatsApp Business dentro Flusia',
    it: { kicker: 'WHATSAPP BUSINESS', title: 'Rispondi ai clienti senza uscire dal CRM.', desc: 'WhatsApp Business API dentro Flusia: chat, template e risposte automatiche.' },
    en: { kicker: 'WHATSAPP BUSINESS', title: 'Reply to clients without leaving the CRM.', desc: 'WhatsApp Business API inside Flusia: chats, templates and automatic replies.' },
  },
  {
    key: 'google',
    src: '/register-cards/card-google.webp',
    alt: 'Integrazione Google Calendar e Gmail',
    it: { kicker: 'INTEGRAZIONI GOOGLE', title: 'Calendar e Gmail, già collegati.', desc: 'Si aggancia a quello che usi già: appuntamenti ed email sempre in sync.' },
    en: { kicker: 'GOOGLE INTEGRATIONS', title: 'Calendar and Gmail, already connected.', desc: 'It hooks into what you already use: appointments and email always in sync.' },
  },
  {
    key: 'portal',
    src: '/register-cards/card-portal.webp',
    alt: 'Portale cliente di Flusia',
    it: { kicker: 'PORTALE CLIENTE', title: 'Il tuo cliente vede tutto. Senza chiamarti.', desc: "Progetti, documenti e avanzamento in un'area riservata, sempre aggiornata." },
    en: { kicker: 'CLIENT PORTAL', title: 'Your client sees everything. Without calling you.', desc: 'Projects, documents and progress in a private area, always up to date.' },
  },
  {
    key: 'provvigioni',
    src: '/register-cards/card-provvigioni.webp',
    alt: 'Calcolo provvigioni agenti in Flusia',
    it: { kicker: 'PROVVIGIONI', title: 'Le commissioni si calcolano da sole.', desc: 'Per agente, per servizio, multi-livello. Da pagare e pagate, sempre chiare.' },
    en: { kicker: 'COMMISSIONS', title: 'Commissions calculate themselves.', desc: 'Per agent, per service, multi-level. Due and paid, always clear.' },
  },
  {
    key: 'prenotazioni',
    src: '/register-cards/card-prenotazioni.webp',
    alt: 'Pagina pubblica di prenotazione appuntamenti',
    it: { kicker: 'PRENOTAZIONI', title: 'I clienti prenotano da soli.', desc: 'Pagina pubblica con i tuoi orari e tipi di evento. Tu non rincorri più nessuno.' },
    en: { kicker: 'BOOKINGS', title: 'Clients book on their own.', desc: 'A public page with your hours and event types. No more chasing anyone.' },
  },
  {
    key: 'newsletter',
    src: '/register-cards/card-newsletter.webp',
    alt: 'Editor newsletter di Flusia',
    it: { kicker: 'EMAIL & NEWSLETTER', title: "Newsletter che sembrano fatte da un'agenzia.", desc: 'Editor a blocchi, anteprima e invio. Tutto dentro Flusia.' },
    en: { kicker: 'EMAIL & NEWSLETTER', title: 'Newsletters that look agency-made.', desc: 'Block editor, preview and send. All inside Flusia.' },
  },
  {
    key: 'form',
    src: '/register-cards/card-form.webp',
    alt: 'Costruttore form per il sito',
    it: { kicker: 'FORM PER IL SITO', title: 'Cattura lead dal sito, senza sviluppatori.', desc: 'Form e moduli in due minuti. I contatti entrano dritti nel CRM.' },
    en: { kicker: 'WEBSITE FORMS', title: 'Capture leads from your site, no developers.', desc: 'Build forms in two minutes. Contacts land straight in the CRM.' },
  },
  {
    key: 'qrcode',
    src: '/register-cards/card-qrcode.webp',
    alt: 'Generatore di QR code dinamici',
    it: { kicker: 'QR CODE DINAMICI', title: 'QR che cambi quando vuoi.', desc: 'Tracciabili e modificabili in ogni momento. Scansioni e destinazioni sotto controllo.' },
    en: { kicker: 'DYNAMIC QR CODES', title: 'QR codes you can change anytime.', desc: 'Trackable and editable at any moment. Scans and destinations under control.' },
  },
  {
    key: 'video',
    src: '/register-cards/card-video.webp',
    alt: 'Registrazione video schermo e webcam',
    it: { kicker: 'VIDEO MESSAGGI', title: 'Registra schermo e webcam. Condividi un link.', desc: 'Stile Loom, dentro Flusia. Spieghi, vendi e supporti in un video.' },
    en: { kicker: 'VIDEO MESSAGES', title: 'Record screen and webcam. Share a link.', desc: 'Loom-style, inside Flusia. Explain, sell and support in a video.' },
  },
  {
    key: 'wiki',
    src: '/register-cards/card-wiki.webp',
    alt: 'Wiki e knowledge base aziendale',
    it: { kicker: 'WIKI & PROCESSI', title: "Il sapere dell'azienda, in un posto solo.", desc: 'Manuali, procedure e knowledge base, sempre a portata di team.' },
    en: { kicker: 'WIKI & PROCESSES', title: "Your company's knowledge, in one place.", desc: "Manuals, procedures and knowledge base, always at the team's fingertips." },
  },
  {
    key: 'invoicing',
    src: '/register-cards/card-invoicing.webp',
    alt: 'Coda di fatturazione automatica in Flusia',
    it: { kicker: 'FATTURAZIONE E PREVISIONALE', title: 'A contratto firmato, la fattura parte da sola.', desc: 'Niente fogli da rincorrere, niente scadenze dimenticate. Sync Fatture in Cloud.' },
    en: { kicker: 'INVOICING & FORECAST', title: 'Contract signed, the invoice goes out on its own.', desc: 'No spreadsheets to chase, no missed deadlines. Fatture in Cloud sync.' },
  },
];
